/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { useUI } from "../theme/ThemeProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  fetchCurrentTenantPlan,
  isFreeOrTrialPlan,
  isPlanExpired,
} from "../utils/subscriptionPlan";
import { TRIAL_WELCOME_KEY } from "../../pages/utils/completeStoreOnboarding";

export default function WhatsLayout({ title, children, hideNavbar = false }) {
  const { theme, lang } = useUI();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const isArabic = lang === "ar";

  useEffect(() => {
    let cancelled = false;

    const checkExpiration = async () => {
      const isAllowedPath =
        location.pathname.includes("/subscriptions") ||
        location.pathname.includes("/payment") ||
        location.pathname.includes("/theme-plan") ||
        location.pathname.includes("/reset-password") ||
        location.pathname.includes("/403");

      if (localStorage.getItem("subscriptionExpired") === "true" && !isAllowedPath) {
        navigate("/theme-plan", { replace: true });
        return;
      }

      try {
        const plan = await fetchCurrentTenantPlan();
        if (cancelled) return;

        // الحساب الجديد يأخذ الخطة المجانية تلقائياً أثناء التسجيل
        if (!plan) return;

        if (!isPlanExpired(plan)) return;

        const isFreeOrTrial = isFreeOrTrialPlan(plan);
        const alertKey = isFreeOrTrial ? "trialExpiredAlertShown" : "paidExpiredAlertShown";

        if (!sessionStorage.getItem(alertKey)) {
          sessionStorage.setItem(alertKey, "1");
          Swal.fire({
            icon: "warning",
            title: isArabic ? "انتهت الخطة" : "Plan Expired",
            text: isArabic
              ? (isFreeOrTrial
                ? "انتهت مدة التجربة/الخطة المجانية. يرجى الاشتراك في باقة لمتابعة العمل."
                : "انتهت مدة الاشتراك. يرجى تجديد الاشتراك للمتابعة.")
              : (isFreeOrTrial
                ? "Your free trial/plan has expired. Please subscribe to continue."
                : "Your subscription has expired. Please renew your plan to continue."),
            confirmButtonText: isArabic ? "حسنًا" : "OK",
            confirmButtonColor: "#7400BF",
            background: isDark ? "#1E293B" : "#fff",
            color: isDark ? "#fff" : "#000",
          });
        }

        if (!isAllowedPath) navigate("/theme-plan", { replace: true });
      } catch (err) {
        console.warn("Could not verify plan expiry from API:", err);
      }
    };

    checkExpiration();
    return () => {
      cancelled = true;
    };
  }, [navigate, isArabic, isDark, location.pathname]);

  useEffect(() => {
    if (sessionStorage.getItem(TRIAL_WELCOME_KEY) !== "1") return;
    sessionStorage.removeItem(TRIAL_WELCOME_KEY);
    toast.info(
      isArabic ? "تم الدخول بالاشتراك التجريبي" : "You signed in with the trial subscription",
      {
        position: "top-center",
        rtl: isArabic,
        autoClose: 4500,
      }
    );
  }, [isArabic]);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) setSidebarOpen(false); // Close mobile sidebar when resizing to desktop
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (!isDesktop && sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [sidebarOpen, isDesktop]);

  return (
    <div
      dir={lang === "ar" ? "rtl" : "ltr"}
      className={`flex min-h-screen w-full ${isDark ? "bg-[#0B1020] text-gray-100" : "bg-[#F9FAFB] text-gray-900"
        }`}
      style={{
        position: "relative",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <ToastContainer position="top-center" rtl={isArabic} newestOnTop />

      {/* ===== Overlay للشاشات الصغيرة ===== */}
      {!isDesktop && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] transition-opacity duration-300 animate-in fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ===== Sidebar ===== */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isAlwaysVisible={isDesktop}
      />

      {/* ===== المحتوى الرئيسي ===== */}
      <div
        className={`flex flex-col flex-1 transition-all duration-500 ease-in-out ${isDesktop
            ? lang === "ar"
              ? "mr-64"
              : "ml-64"
            : ""
          }`}
        style={{
          width: "100%",
          minWidth: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!hideNavbar && (
          <Navbar
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            isSidebarOpen={sidebarOpen}
          />
        )}

        <main
          className="flex-1 w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-6"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {title && (
            <h2
              className={`text-xl sm:text-2xl font-extrabold mb-4 sm:mb-6 tracking-tight ${isDark ? "text-[#F2B441]" : "text-[#21003D]"
                }`}
            >
              {title}
            </h2>
          )}

          {/* منطقة المحتوى */}
          <div
            className="w-full flex-1"
            style={{
              paddingBottom: "80px", // Reduced padding for mobile
            }}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
